import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '@/store/auth';
import { useTenantTheme } from '@/lib/useTenantTheme';
import { Shell } from '@/components/Shell';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { QuickAddPage } from '@/pages/QuickAddPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { SearchPage } from '@/pages/SearchPage';
import { CustomersPage } from '@/pages/CustomersPage';
import { BookingPage } from '@/pages/BookingPage';
import { BillingPage } from '@/pages/BillingPage';
import { SchedulingPage } from '@/pages/SchedulingPage';
import { CompliancePage } from '@/pages/CompliancePage';
import { PhotosPage } from '@/pages/PhotosPage';
import { LinePage } from '@/pages/LinePage';
import { LineFunnelPage } from '@/pages/LineFunnelPage';
import { CatalogPage } from '@/pages/CatalogPage';
import { InventoryPage } from '@/pages/InventoryPage';
import { SuppliersPage } from '@/pages/SuppliersPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { CommissionPage } from '@/pages/CommissionPage';
import { DailyClosePage } from '@/pages/DailyClosePage';
import { CheckoutPage } from '@/pages/CheckoutPage';
import { FunnelPage } from '@/pages/FunnelPage';
import { RecallPage } from '@/pages/RecallPage';
import { UtilizationPage } from '@/pages/UtilizationPage';
import { BranchPnlPage } from '@/pages/BranchPnlPage';
import { ClinicToolsPage } from '@/pages/ClinicToolsPage';
import { AftercarePage } from '@/pages/AftercarePage';
import { GrowthPage } from '@/pages/GrowthPage';
import { PlatformPage } from '@/pages/PlatformPage';
import { AuditPage } from '@/pages/AuditPage';
import { StaffPage } from '@/pages/StaffPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { AdvancedPage } from '@/pages/AdvancedPage';
import { PlatformShell } from '@/components/PlatformShell';
import { PlatformTenantsPage } from '@/pages/PlatformTenantsPage';

export function App() {
  const { status, bootstrap, user } = useAuth();
  useTenantTheme(status === 'authed' && user?.type !== 'platform');

  useEffect(() => {
    if (status === 'idle') void bootstrap();
  }, [status, bootstrap]);

  if (status === 'idle' || status === 'loading') {
    return <div style={{ padding: 40, color: 'var(--ink-3)' }}>載入中…</div>;
  }

  if (status === 'anon') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // 平台營運方 → 平台主控台（與租戶後台分流）
  if (user?.type === 'platform') {
    return (
      <PlatformShell>
        <Routes>
          <Route path="/platform/tenants" element={<PlatformTenantsPage />} />
          <Route path="/platform" element={<PlatformPage />} />
          <Route path="*" element={<Navigate to="/platform/tenants" replace />} />
        </Routes>
      </PlatformShell>
    );
  }

  return (
    <Shell>
      <Routes>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/quickadd" element={<QuickAddPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/advanced" element={<AdvancedPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/booking" element={<BookingPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/scheduling" element={<SchedulingPage />} />
        <Route path="/compliance" element={<CompliancePage />} />
        <Route path="/photos" element={<PhotosPage />} />
        <Route path="/line" element={<LinePage />} />
        <Route path="/line-funnel" element={<LineFunnelPage />} />
        <Route path="/catalog" element={<CatalogPage />} />
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/suppliers" element={<SuppliersPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/commission" element={<CommissionPage />} />
        <Route path="/daily-close" element={<DailyClosePage />} />
        <Route path="/funnel" element={<FunnelPage />} />
        <Route path="/recall" element={<RecallPage />} />
        <Route path="/utilization" element={<UtilizationPage />} />
        <Route path="/branch-pnl" element={<BranchPnlPage />} />
        <Route path="/clinic-tools" element={<ClinicToolsPage />} />
        <Route path="/aftercare" element={<AftercarePage />} />
        <Route path="/growth" element={<GrowthPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/staff" element={<StaffPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Shell>
  );
}
